import {
  FETCH_CPO_SETTLEMENT_BATCHES,
  GET_CPO_SETTLEMENT_BATCH,
  APPROVE_CPO_SETTLEMENT_BATCH,
  REJECT_CPO_SETTLEMENT_BATCH,
  PAY_CPO_SETTLEMENT_BATCH,
  REFRESH_CPO_SETTLEMENT_BATCH,
  RUN_CPO_SETTLEMENT_CYCLE,
} from 'actions/partnerManagement';
import { toast } from 'react-toastify';

/**
 * CDB-1597 — outbound settlement to CPO partners.
 *
 * Three separate loading flags, because they guard different parts of the screen:
 * `isLoading` is the list, `isActionLoading` is one row's action so approving or
 * paying does not blank the whole table, and `isDetailLoading` is the drill-down
 * modal so opening it does not put the table back into a skeleton state.
 */
const initialState = {
  isLoading: false,
  isActionLoading: false,
  isDetailLoading: false,
  batches: [],
  page: 1,
  limit: 10,
  totalPages: 1,
  total: 0,
  batchDetail: null,
  batchLines: [],
  batchTotals: null,
  reconciles: null,
  // The session lines are paginated server-side. Tracked here because a batch can
  // legitimately hold more sessions than one page, and without this the drill-down
  // showed only the first page while appearing to be the complete breakdown.
  linesPage: 1,
  linesTotalPages: 1,
  linesTotal: 0,
  // Which single row is being re-checked with the provider. Held per batch rather
  // than as a page-wide flag so refreshing one settlement does not disable the
  // actions on every other row.
  refreshingBatchId: null,
};

const cpoSettlementReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case FETCH_CPO_SETTLEMENT_BATCHES.REQUEST: {
      return { ...state, isLoading: true };
    }

    // The previous settlement's sessions are cleared as the request goes out.
    // Keeping them meant the modal briefly showed the sessions and amounts of the
    // row opened before this one — on a payments screen that is worse than showing
    // nothing, because the figures look authoritative while belonging to another
    // partner's payment.
    case GET_CPO_SETTLEMENT_BATCH.REQUEST: {
      return {
        ...state,
        isDetailLoading: true,
        batchDetail: null,
        batchLines: [],
        batchTotals: null,
        reconciles: null,
        // Reset too, so the footer cannot momentarily show the previously opened
        // settlement's page count against this one's lines.
        linesPage: 1,
        linesTotalPages: 1,
        linesTotal: 0,
      };
    }

    case APPROVE_CPO_SETTLEMENT_BATCH.REQUEST:
    case REJECT_CPO_SETTLEMENT_BATCH.REQUEST:
    case PAY_CPO_SETTLEMENT_BATCH.REQUEST:
    case RUN_CPO_SETTLEMENT_CYCLE.REQUEST: {
      return { ...state, isActionLoading: true };
    }

    case FETCH_CPO_SETTLEMENT_BATCHES.SUCCESS: {
      return {
        ...state,
        isLoading: false,
        batches: payload.results,
        page: payload.page,
        limit: payload.limit,
        totalPages: payload.totalPages,
        total: payload.totalResults,
      };
    }

    case GET_CPO_SETTLEMENT_BATCH.SUCCESS: {
      return {
        ...state,
        isDetailLoading: false,
        batchDetail: payload.batch,
        batchLines: payload.lines?.results || [],
        linesPage: payload.lines?.page || 1,
        linesTotalPages: payload.lines?.totalPages || 1,
        linesTotal: payload.lines?.totalResults || 0,
        batchTotals: payload.totals,
        // Surfaced rather than hidden: if the session lines do not sum to the
        // payment amount, that must be visible in the UI.
        reconciles: payload.reconciles,
      };
    }

    // Approving now also pays, so the message says which of the two happened. An
    // approval whose payment did not go out must not read as a completed payment —
    // the settlement stays cleared and the next daily run will retry it.
    case APPROVE_CPO_SETTLEMENT_BATCH.SUCCESS: {
      const outcome = payload?.payment?.outcome;
      if (outcome === 'initiated') {
        toast.success('Approved — payment sent to the partner');
      } else if (outcome === 'provider_not_configured') {
        toast.warning('Approved, but no payment provider is configured — it will be paid once one is set up');
      } else if (outcome === 'needs_review') {
        toast.error('Approved, but the payment outcome is unknown — check the payment provider before retrying');
      } else {
        toast.warning('Approved. The payment was not sent yet — the next daily run will attempt it');
      }
      return { ...state, isActionLoading: false };
    }

    case REJECT_CPO_SETTLEMENT_BATCH.SUCCESS: {
      toast.success('Settlement rejected — sessions returned for a later cycle');
      return { ...state, isActionLoading: false };
    }

    case PAY_CPO_SETTLEMENT_BATCH.SUCCESS: {
      toast.success('Payment initiated');
      return { ...state, isActionLoading: false };
    }

    case REFRESH_CPO_SETTLEMENT_BATCH.REQUEST: {
      return { ...state, refreshingBatchId: payload };
    }

    // Reports what the provider actually said. "Refreshed" alone would read as
    // progress even when the commonest honest answer is that the bank is still
    // working on it, which is the case this button exists to reveal.
    case REFRESH_CPO_SETTLEMENT_BATCH.SUCCESS: {
      const status = payload?.batch?.status;
      if (payload?.outcome === 'still_processing') {
        toast.info('Still in progress at the bank — no change yet');
      } else if (status) {
        toast.success(`Status updated to ${String(status).replace(/_/g, ' ')}`);
      } else {
        toast.success('Status refreshed');
      }
      return { ...state, refreshingBatchId: null };
    }

    // Deliberately quiet when the server answered at all. The api client's
    // interceptor already toasts `error.response.data.message` for any error
    // carrying a response, so saying it again here shows the same failure twice.
    //
    // Note the shape: that interceptor rejects with `error.response`, so what
    // arrives here IS the response — there is no nested `.response` to read, and
    // looking for one meant every message fell through to a generic line.
    //
    // The case worth announcing is the one the interceptor cannot see, because its
    // own check needs a response: a request that got none. That matters here rather
    // than being noise — the refresh may still have reached the provider, so the
    // row is not wrong, only unconfirmed.
    case REFRESH_CPO_SETTLEMENT_BATCH.FAIL: {
      if (!payload?.error?.data?.message) {
        toast.error('Could not reach the server — this settlement is unchanged.');
      }
      return { ...state, refreshingBatchId: null };
    }

    // Reports what the run actually did. A flat "run complete" was misleading:
    // the commonest real outcome is that there was nothing to settle, because
    // every matured session is already inside an existing settlement — which looks
    // identical to the run having failed unless it says so.
    case RUN_CPO_SETTLEMENT_CYCLE.SUCCESS: {
      const summary = payload?.summary || {};
      const created = summary.batch_created || 0;
      // The payment phase covers every payable settlement, not only the ones this
      // run created, so a run that created nothing can still legitimately have paid
      // something that was waiting. Both numbers are reported.
      const paid = payload?.payment?.summary?.initiated || 0;
      const held = summary.batch_created ? payload?.results?.filter((r) => r?.approval_required).length || 0 : 0;

      if (created > 0 || paid > 0) {
        const parts = [];
        if (created > 0) parts.push(created === 1 ? '1 settlement generated' : `${created} settlements generated`);
        if (paid > 0) parts.push(`${paid} paid`);
        if (held > 0) parts.push(`${held} awaiting approval`);
        toast.success(parts.join(' · '));
      } else if (summary.already_run) {
        toast.info('Already run for this partner today — a settlement for today exists');
      } else if (summary.below_minimum) {
        toast.info('Total is below the configured minimum — the sessions roll into a later cycle');
      } else if (summary.nothing_to_settle) {
        toast.info('Nothing new to settle — matured sessions are already in a settlement or being held');
      } else if (summary.failed) {
        toast.error('The run could not be completed for this partner');
      } else {
        toast.success('Settlement run complete');
      }
      return { ...state, isActionLoading: false };
    }

    case FETCH_CPO_SETTLEMENT_BATCHES.FAIL: {
      return { ...state, isLoading: false };
    }

    case GET_CPO_SETTLEMENT_BATCH.FAIL: {
      return { ...state, isDetailLoading: false };
    }

    // The server rejects an action that is no longer valid — for example a batch
    // someone else has already approved — with a 409 and a message. Show that
    // message rather than a generic failure, because it tells the user what to do.
    case APPROVE_CPO_SETTLEMENT_BATCH.FAIL:
    case REJECT_CPO_SETTLEMENT_BATCH.FAIL:
    case PAY_CPO_SETTLEMENT_BATCH.FAIL:
    case RUN_CPO_SETTLEMENT_CYCLE.FAIL: {
      const message = payload?.error?.response?.data?.message;
      toast.error(message || 'Action could not be completed');
      return { ...state, isActionLoading: false };
    }

    default:
      return state;
  }
};

export default cpoSettlementReducer;
